<template>
  <div class="model-selector">
    <!-- Header -->
    <div class="row items-center justify-between q-mb-md">
      <div class="text-h6">
        {{ t('core.AiDocumentProcessor.modelSelection.title') }}
      </div>

      <div
        v-if="selectedQueueItem"
        class="text-caption text-grey-6">
        {{ t('core.AiDocumentProcessor.modelSelection.queueId') }}: {{ selectedQueueItem.id }}
      </div>
    </div>

    <!-- No Queue Item Selected -->
    <div
      v-if="!selectedQueueItem"
      class="text-center q-pa-xl">
      <q-icon
        :name="symRoundedModelTraining"
        size="64px"
        color="grey-4"
        class="q-mb-md" />
      <div class="text-h6 text-grey-6 q-mb-sm">{{ t('core.AiDocumentProcessor.modelSelection.noDocument') }}</div>
      <div class="text-body2 text-grey-5">
        {{ t('core.AiDocumentProcessor.modelSelection.selectDocumentHint') }}
      </div>
    </div>

    <!-- Model Selection Interface -->
    <div v-else>
      <!-- AI Suggestions Section -->
      <div
        v-if="hasSuggestions"
        class="suggestions-section q-mb-lg">
        <div class="row items-center q-mb-md">
          <q-icon
            :name="symRoundedAutoAwesome"
            class="q-mr-sm"
            color="primary" />
          <div class="text-h6">{{ t('core.AiDocumentProcessor.modelSelection.aiSuggestions') }}</div>
          <q-space />

          <!-- Auto-confirm button for high confidence -->
          <q-btn
            v-if="isAutoConfirmable"
            color="positive"
            size="sm"
            :icon="symRoundedCheckCircle"
            :label="t('core.AiDocumentProcessor.modelSelection.autoConfirm')"
            :loading="isConfirming"
            @click="autoConfirm">
            <q-tooltip>{{ t('core.AiDocumentProcessor.modelSelection.autoConfirmTooltip') }}</q-tooltip>
          </q-btn>
        </div>

        <!-- Suggestion Cards -->
        <div class="row q-gutter-md">
          <div
            v-for="(suggestion, index) in suggestionsWithModels"
            :key="suggestion.model"
            class="col-12 col-md-6">
            <q-card
              class="suggestion-card"
              :class="{
                'suggestion-card--selected': selectedModelKey === suggestion.model,
                'suggestion-card--best': index === 0,
                'suggestion-card--high-confidence': suggestion.confidence >= confidenceThresholds.high,
              }"
              clickable
              @click="selectModel(suggestion.model)">
              <q-card-section class="q-pa-md">
                <div class="row items-start q-gutter-md">
                  <!-- Model Icon -->
                  <div class="model-icon-container">
                    <q-icon
                      :name="modelIcons[suggestion.model] || 'symRoundedSmartToy'"
                      size="32px"
                      color="primary" />

                    <!-- Rank Badge -->
                    <q-badge
                      v-if="index === 0"
                      color="positive"
                      floating
                      rounded
                      :label="t('core.AiDocumentProcessor.modelSelection.autoConfirmed')" />
                  </div>

                  <!-- Model Info -->
                  <div class="col">
                    <div class="text-h6 q-mb-xs">
                      {{ suggestion.modelInfo?.name || suggestion.model }}
                    </div>

                    <div class="text-body2 text-grey-6 q-mb-sm">
                      {{ suggestion.modelInfo?.description || t('core.AiDocumentProcessor.modelSelection.noSuggestions') }}
                    </div>

                    <!-- Confidence Score -->
                    <div class="confidence-section">
                      <div class="row items-center q-gutter-sm q-mb-xs">
                        <q-chip
                          :color="getConfidenceColor(suggestion.confidence)"
                          text-color="white"
                          size="sm"
                          :icon="symRoundedPsychology">
                          {{ getConfidenceLabel(suggestion.confidence) }}
                        </q-chip>
                      </div>

                      <!-- Progress Bar -->
                      <q-linear-progress
                        :value="suggestion.confidence"
                        :color="getConfidenceColor(suggestion.confidence)"
                        size="6px"
                        rounded
                        class="q-mb-sm" />

                      <!-- Reason -->
                      <div class="text-caption text-grey-6">
                        {{ suggestion.reason }}
                      </div>
                    </div>
                  </div>

                  <!-- Selection Indicator -->
                  <div class="selection-indicator">
                    <q-icon
                      v-if="selectedModelKey === suggestion.model"
                      :name="symRoundedCheckCircle"
                      size="24px"
                      color="positive" />
                  </div>
                </div>
              </q-card-section>
            </q-card>
          </div>
        </div>
      </div>

      <!-- All Models Section -->
      <div class="all-models-section">
        <div class="row items-center justify-between q-mb-md">
          <div class="text-h6">{{ t('core.AiDocumentProcessor.modelSelection.allModels') }}</div>

          <!-- Search -->
          <div class="search-container">
            <q-input
              v-model="searchQuery"
              outlined
              dense
              :placeholder="t('core.AiDocumentProcessor.modelSelection.searchModels')"
              clearable
              style="min-width: 200px">
              <template #prepend>
                <q-icon :name="symRoundedSearch" />
              </template>
            </q-input>
          </div>
        </div>

        <!-- Model Grid -->
        <div class="models-grid">
          <div
            v-for="model in filteredModels"
            :key="model.key"
            class="model-card-container">
            <q-card
              class="model-card"
              :class="{
                'model-card--selected': selectedModelKey === model.key,
                'model-card--suggested': isModelSuggested(model.key),
              }"
              clickable
              @click="selectModel(model.key)">
              <q-card-section class="q-pa-md">
                <div class="row items-start q-gutter-md">
                  <!-- Model Icon -->
                  <div class="model-icon-container">
                    <q-icon
                      :name="modelIcons[model.key] || 'symRoundedSmartToy'"
                      size="28px"
                      :color="selectedModelKey === model.key ? 'primary' : 'grey-6'" />

                    <!-- Suggestion Badge -->
                    <q-badge
                      v-if="isModelSuggested(model.key)"
                      color="primary"
                      floating
                      rounded
                      :label="`#${getSuggestionRank(model.key)}`" />
                  </div>

                  <!-- Model Info -->
                  <div class="col">
                    <div class="text-body1 text-weight-medium q-mb-xs">
                      {{ model.name }}
                    </div>

                    <div class="text-caption text-grey-6">
                      {{ model.description }}
                    </div>

                    <!-- Suggestion Info -->
                    <div
                      v-if="isModelSuggested(model.key)"
                      class="suggestion-info q-mt-xs">
                      <q-chip
                        :color="getConfidenceColor(getModelSuggestion(model.key)?.confidence || 0)"
                        text-color="white"
                        size="sm"
                        dense>
                        {{ getConfidenceLabel(getModelSuggestion(model.key)?.confidence || 0) }}
                      </q-chip>
                    </div>
                  </div>

                  <!-- Selection Indicator -->
                  <div class="selection-indicator">
                    <q-icon
                      v-if="selectedModelKey === model.key"
                      :name="symRoundedRadioButtonChecked"
                      size="20px"
                      color="primary" />
                    <q-icon
                      v-else
                      :name="symRoundedRadioButtonUnchecked"
                      size="20px"
                      color="grey-4" />
                  </div>
                </div>
              </q-card-section>
            </q-card>
          </div>
        </div>

        <!-- No Models Found -->
        <div
          v-if="filteredModels.length === 0"
          class="text-center q-pa-lg">
          <q-icon
            :name="symRoundedSearchOff"
            size="48px"
            color="grey-4"
            class="q-mb-md" />
          <div class="text-body1 text-grey-6">
            {{ t('core.AiDocumentProcessor.modelSelection.noResults', { query: searchQuery }) }}
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="actions-section q-mt-lg">
        <div class="row items-center justify-between">
          <!-- Selected Model Info -->
          <div
            v-if="selectedModel"
            class="selected-model-info">
            <div class="text-caption text-grey-6">{{ t('core.AiDocumentProcessor.modelSelection.selectedModel') }}</div>
            <div class="text-body1 text-weight-medium">
              {{ selectedModel.name }}
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="row q-gutter-md">
            <q-btn
              v-if="hasSuggestions"
              color="grey-6"
              flat
              :label="t('core.AiDocumentProcessor.modelSelection.resetToBest')"
              :disable="!bestSuggestion"
              @click="resetToBestSuggestion" />

            <q-btn
              color="primary"
              :loading="isConfirming"
              :disable="!canConfirm"
              :icon="symRoundedCheckCircle"
              :label="t('core.AiDocumentProcessor.modelSelection.confirmSelection')"
              @click="confirmModel" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  symRoundedCheckCircle,
  symRoundedPsychology,
  symRoundedRadioButtonChecked,
  symRoundedRadioButtonUnchecked,
  symRoundedSearch,
  symRoundedSearchOff,
} from '@quasar/extras/material-symbols-rounded'
import useCrudUtils from 'src/CrudManager/Core/composables/CrudUtils/useCrudUtils'
import { useModelSelection } from 'src/CrudManager/Core/composites/AiDocumentProcessor/composables/useModelSelection'
import type { AiQueueItem } from '../types'
import { computed, ref, watch } from 'vue'

// Component props
export interface ModelSelectorProps {
  readonly queueItem?: AiQueueItem
}

// Component events
export interface ModelSelectorEvents {
  'model-confirmed': [queueId: string, modelType: string]
  'model-selected': [modelType: string]
}

const props = withDefaults(defineProps<ModelSelectorProps>(), {
  queueItem: undefined,
})

const emit = defineEmits<ModelSelectorEvents>()

// Model selection composable
const {
  availableModels,
  selectedQueueItem,
  selectedModelKey,
  isConfirming,
  hasSuggestions,
  bestSuggestion,
  suggestionsWithModels,
  selectedModel,
  canConfirm,
  isAutoConfirmable,
  confidenceThresholds,
  setQueueItem,
  clearSelection,
  selectModel,
  confirmModel: confirmModelAction,
  autoConfirm: autoConfirmAction,
  resetToBestSuggestion,
  getConfidenceColor,
  getConfidenceLabel,
  getSuggestionRank,
  filterModels,
  isModelSuggested,
  getModelSuggestion,
} = useModelSelection()

const { getModelIcon } = useCrudUtils()

// Local state
const searchQuery = ref('')

// --- FIX: Async icon resolution ---
const modelIcons = ref<Record<string, string>>({})

async function resolveIcons() {
  const icons: Record<string, string> = {}
  // Gather all unique model keys from availableModels and suggestions
  const allKeys = new Set<string>()
  availableModels.value.forEach((m) => allKeys.add(m.key))
  suggestionsWithModels.value?.forEach((s) => allKeys.add(s.model))
  for (const key of allKeys) {
    try {
      const icon = await getModelIcon(key)
      icons[key] = typeof icon === 'string' ? icon : 'symRoundedSmartToy'
    } catch {
      icons[key] = 'symRoundedSmartToy'
    }
  }
  modelIcons.value = icons
}

watch(
  [availableModels, suggestionsWithModels],
  () => {
    resolveIcons()
  },
  { immediate: true },
)
// --- END FIX ---

// Computed properties
const filteredModels = computed(() => filterModels(searchQuery.value))

// Watch for prop changes
watch(
  () => props.queueItem,
  (newQueueItem) => {
    if (newQueueItem) {
      setQueueItem(newQueueItem)
    } else {
      clearSelection()
    }
  },
  { immediate: true },
)

// Watch for model selection
watch(selectedModelKey, (newModel) => {
  if (newModel) {
    emit('model-selected', newModel)
  }
})

// Methods
const confirmModel = async (): Promise<void> => {
  const success = await confirmModelAction()
  if (success && selectedQueueItem.value) {
    emit('model-confirmed', selectedQueueItem.value.id, selectedModelKey.value)
  }
}

const autoConfirm = async (): Promise<void> => {
  const success = await autoConfirmAction()
  if (success && selectedQueueItem.value) {
    emit('model-confirmed', selectedQueueItem.value.id, selectedModelKey.value)
  }
}
</script>

<style lang="scss" scoped>
.model-selector {
  .suggestion-card {
    transition: all 0.3s ease;
    border-radius: 12px;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    &--selected {
      border: 2px solid var(--q-primary);
      box-shadow: 0 0 0 3px rgba(var(--q-primary-rgb), 0.1);
    }

    &--best {
      position: relative;

      &::before {
        content: '';
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        bottom: -2px;
        background: linear-gradient(45deg, var(--q-positive), var(--q-primary));
        border-radius: 14px;
        z-index: -1;
      }
    }

    &--high-confidence {
      border-left: 4px solid var(--q-positive);
    }
  }

  .model-card {
    transition: all 0.3s ease;
    border-radius: 8px;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    &--selected {
      border: 2px solid var(--q-primary);
      background: var(--q-primary-1);
    }

    &--suggested {
      border-left: 3px solid var(--q-primary);
    }
  }

  .models-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 16px;
  }

  .model-icon-container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .confidence-section {
    .q-linear-progress {
      opacity: 0.8;
    }
  }

  .search-container {
    min-width: 200px;
  }

  .selection-indicator {
    display: flex;
    align-items: center;
  }

  .selected-model-info {
    max-width: 300px;
  }
}

// Dark mode support
.body--dark {
  .model-selector {
    .suggestion-card--selected,
    .model-card--selected {
      background: rgba(var(--q-primary-rgb), 0.1);
    }
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .model-selector {
    .models-grid {
      grid-template-columns: 1fr;
    }

    .suggestions-section .row {
      flex-direction: column;
    }

    .actions-section .row {
      flex-direction: column;
      align-items: stretch;

      .selected-model-info {
        max-width: none;
        margin-bottom: 16px;
      }
    }
  }
}
</style>
